/**
 * Environment gate for the Sanity adapter.
 *
 * DEFAULT-DENY: Sanity is only ever selected on an explicit allowlist of
 * environments that never reach customers: development, test and local.
 * Acceptance and production (the path to customers), or any unknown value,
 * return `false`. This lets the adapter be merged to `main` and exercised
 * locally, on development and on test without any risk of it activating on a
 * customer-facing environment. Test is on the list because that is where a
 * change is tested before it merges; acceptance is not, because whatever runs
 * there goes on to production.
 *
 * Pass `env('ENVIRONMENT')` (from `@eneco/common/env`). An empty string is
 * treated as local development (the env var is unset when running locally).
 *
 * `allowSharedEnvironment` is a per-container opt-in (default `false`): a
 * container that has explicitly decided it's ready to serve the `?_cms=`
 * override on acceptance/production (every wired container today, see
 * `containers/gb/eneco/main/cms-config.ts`) passes `true` here to additionally
 * allow those two shared environments. `FE_ENVIRONMENT` itself is set
 * DTAP-wide (identical for every container in a given tier), so this flag —
 * not the environment name — is what keeps the widening scoped to one
 * container.
 */
const ENABLED_ENVIRONMENTS: readonly string[] = ['development', 'test', 'local', ''];
const SHARED_ENVIRONMENTS: readonly string[] = ['acceptance', 'production'];

/**
 * The environment names the gate knows. The defaults are Eneco NL's DTAP names; a host whose
 * environments are named differently passes its own, and then only those count — a list it
 * leaves out is empty, not NL's. Matched case- and whitespace-insensitively; `''` means unset.
 */
export interface SanityEnvironments {
  /** Always allowed. */
  enabled?: readonly string[];
  /** Allowed only with `allowSharedEnvironment`. */
  shared?: readonly string[];
}

const normalize = (name: string): string => name.trim().toLowerCase();

/** Environment-name gate — true on development/test/local/unset, or on acceptance/production when opted in. */
export const isSanityEnabled = (
  environment: string | undefined,
  allowSharedEnvironment = false,
  environments?: SanityEnvironments,
): boolean => {
  const enabled = environments ? (environments.enabled ?? []) : ENABLED_ENVIRONMENTS;
  const shared = environments ? (environments.shared ?? []) : SHARED_ENVIRONMENTS;
  const current = normalize(environment ?? '');
  const matches = (names: readonly string[]) => names.some(name => normalize(name) === current);
  return matches(enabled) || (allowSharedEnvironment && matches(shared));
};

export interface SanityGuardInput {
  environment: string | undefined;
  projectId: string | undefined;
  dataset: string | undefined;
  /** See `isSanityEnabled` — per-container opt-in for acceptance/production. Default `false`. */
  allowSharedEnvironment?: boolean;
  /** See `SanityEnvironments`. Defaults to Eneco NL's environment names. */
  environments?: SanityEnvironments;
}

/**
 * The full guard a container should use to decide whether to inject the Sanity
 * adapter. Fail-safe: returns `true` ONLY when the environment is enabled AND
 * the required Sanity config is present. If the env vars are unset (the state
 * on `main` until someone configures them), this returns `false` and the
 * container falls back to Sitecore — so a partial rollout never breaks pages.
 */
export const shouldUseSanityAdapter = ({
  environment,
  projectId,
  dataset,
  allowSharedEnvironment,
  environments,
}: SanityGuardInput): boolean =>
  isSanityEnabled(environment, allowSharedEnvironment, environments) &&
  Boolean(projectId?.trim()) &&
  Boolean(dataset?.trim());
